import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, inject } from '@angular/core';
import { environment } from '../../../environments/environment';

const CF_BEACON_SRC = 'https://static.cloudflareinsights.com/beacon.min.js';
const GA_SRC_PREFIX = 'https://www.googletagmanager.com/gtag/js?id=';

/** Un refus est redemandé après 1 semaine ; un accord n'expire pas (seul l'utilisateur le retire via « Gérer les cookies »). */
export const CONSENT_DENIED_TTL_DAYS = 7;
/** Durée de vie des cookies `_ga` : 13 mois, le maximum recommandé (défaut GA : 2 ans). */
export const GA_COOKIE_DAYS = 395;

/**
 * Charge le beacon Cloudflare Web Analytics (statistiques anonymes, sans cookie : aucun
 * consentement requis).
 *
 * - Navigateur seulement : rien n'est injecté pendant le rendu SSR.
 * - Désactivé si `features.analytics` est faux ou si `cloudflareAnalyticsToken` est vide.
 * - Le beacon suit lui-même les changements de route de l'application (SPA).
 * - CSP nginx du site public : `static.cloudflareinsights.com` (script-src) et
 *   `cloudflareinsights.com` (connect-src).
 *
 * À appeler dans un `provideAppInitializer`.
 */
export function loadCloudflareWebAnalytics(): void {
  const token = environment.cloudflareAnalyticsToken;
  if (!environment.features.analytics || !token) return;
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return;

  const doc = inject(DOCUMENT);
  if (doc.querySelector(`script[src="${CF_BEACON_SRC}"]`)) return;

  const script = doc.createElement('script');
  script.type = 'module';
  script.src = CF_BEACON_SRC;
  script.setAttribute('data-cf-beacon', JSON.stringify({ token }));
  doc.head.appendChild(script);
}

type GtagWindow = Window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  [key: string]: unknown;
};

/**
 * Charge Google Analytics 4 (gtag.js). À n'appeler QU'APRÈS le consentement de l'utilisateur
 * (cf. AnalyticsConsentService) : GA dépose des cookies (`_ga`, `_ga_<ID>`).
 *
 * Les changements de route de l'application sont suivis par la « mesure améliorée » de GA4
 * (événements d'historique du navigateur), activée par défaut dans le flux de données.
 * CSP nginx : `*.googletagmanager.com` (script-src) et `*.google-analytics.com`,
 * `*.analytics.google.com`, `*.googletagmanager.com` (connect-src).
 */
export function loadGoogleAnalytics(doc: Document, measurementId: string): void {
  const win = doc.defaultView as GtagWindow | null;
  if (!win || !measurementId) return;

  // Réactive la collecte si l'utilisateur avait refusé puis accepte dans la même visite.
  win[`ga-disable-${measurementId}`] = false;
  if (doc.querySelector(`script[src^="${GA_SRC_PREFIX}"]`)) return;

  win.dataLayer = win.dataLayer || [];
  // gtag.js attend l'objet `arguments` lui-même dans le dataLayer (pas un tableau).
  win.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    win.dataLayer!.push(arguments);
  };
  win.gtag('js', new Date());
  // Cookies limités à 13 mois (défaut GA : 2 ans), durée annoncée sur la page Confidentialité.
  win.gtag('config', measurementId, { cookie_expires: 60 * 60 * 24 * GA_COOKIE_DAYS });

  const script = doc.createElement('script');
  script.async = true;
  script.src = GA_SRC_PREFIX + encodeURIComponent(measurementId);
  doc.head.appendChild(script);
}

/**
 * Coupe Google Analytics (retrait du consentement) : désactive la collecte pour la visite en
 * cours et supprime les cookies `_ga` / `_ga_<ID>` déjà déposés.
 */
export function disableGoogleAnalytics(doc: Document, measurementId: string): void {
  const win = doc.defaultView as GtagWindow | null;
  if (!win || !measurementId) return;

  win[`ga-disable-${measurementId}`] = true;

  const host = win.location.hostname;
  const rootDomain = host.split('.').slice(-2).join('.');
  const domains = ['', host, `.${host}`, `.${rootDomain}`];
  doc.cookie
    .split(';')
    .map(c => c.split('=')[0].trim())
    .filter(name => name === '_ga' || name.startsWith('_ga_'))
    .forEach(name =>
      domains.forEach(domain => {
        doc.cookie = `${name}=; Max-Age=0; path=/${domain ? `; domain=${domain}` : ''}`;
      }),
    );
}
